<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import AppointmentsAPI from 'dashboard/api/appointments';
import StatusBadge from 'dashboard/components-next/appointments/StatusBadge.vue';
import AppointmentBookingModal from 'dashboard/components-next/appointments/AppointmentBookingModal.vue';
import { resolveReportingTimezone } from 'dashboard/routes/dashboard/pipeline/utils/localeFormatting';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  pipelineCardId: {
    type: [Number, String],
    required: true,
  },
  contactId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['appointmentCreated']);

const { t, locale } = useI18n();
const store = useStore();
const currentAccountId = computed(() => {
  const getter = store.getters.getCurrentAccountId;
  return typeof getter === 'function' ? getter() : getter;
});
const reportingTimezone = computed(() => resolveReportingTimezone(store));

// "appointments" → vinculados a ESTE card via pipeline_card_id
// "contactHistory" → todos os agendamentos do contato (qualquer origem),
// usados na seção "Histórico do contato". Deduplicação por id evita
// repetir os do card na seção de histórico.
const appointments = ref([]);
const contactHistory = ref([]);
const loading = ref(false);
const cardAppointmentsError = ref(false);
const contactHistoryError = ref(false);
const showModal = ref(false);
const showAll = ref(false);
const actionLoadingId = ref(null);
const actionLoadingAction = ref(null);
let contextGeneration = 0;
let cardRequestId = 0;
let contactRequestId = 0;
let loadOperationId = 0;
let actionOperationId = 0;
let isUnmounted = false;

const normalizeContextId = value => {
  if (value === undefined || value === null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const captureContext = () => ({
  accountId: normalizeContextId(currentAccountId.value),
  pipelineCardId: normalizeContextId(props.pipelineCardId),
  contactId: normalizeContextId(props.contactId),
  generation: contextGeneration,
});

const isCurrentContext = context =>
  !isUnmounted &&
  context.generation === contextGeneration &&
  context.accountId === normalizeContextId(currentAccountId.value) &&
  context.pipelineCardId === normalizeContextId(props.pipelineCardId) &&
  context.contactId === normalizeContextId(props.contactId);

const resetContextState = () => {
  appointments.value = [];
  contactHistory.value = [];
  loading.value = false;
  cardAppointmentsError.value = false;
  contactHistoryError.value = false;
  showModal.value = false;
  showAll.value = false;
  actionLoadingId.value = null;
  actionLoadingAction.value = null;
};

const upcomingAppointment = computed(() => {
  const now = new Date();
  return (
    appointments.value.find(
      a => new Date(a.scheduled_at) > now && a.status !== 'cancelled'
    ) || null
  );
});

const displayedAppointments = computed(() => {
  if (showAll.value) return appointments.value;
  return appointments.value.slice(0, 3);
});

// Histórico do contato deduplicado: remove tudo que já está em `appointments`
// (vinculado a este card). Mostra appointments criados em outros cards/conversas
// ou diretamente na agenda. Ordenado mais recente primeiro.
const otherContactAppointments = computed(() => {
  if (!contactHistory.value.length) return [];
  const cardIds = new Set(appointments.value.map(a => a.id));
  return contactHistory.value
    .filter(a => !cardIds.has(a.id))
    .slice()
    .sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at));
});

async function loadCardAppointments(context) {
  cardRequestId += 1;
  const requestId = cardRequestId;
  if (isCurrentContext(context)) cardAppointmentsError.value = false;
  try {
    const response = await AppointmentsAPI.list(
      { pipeline_card_id: context.pipelineCardId },
      context.accountId
    );
    if (!isCurrentContext(context) || requestId !== cardRequestId) return;
    const raw = response.data?.data || [];
    appointments.value = [...raw].sort(
      (a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)
    );
  } catch {
    if (isCurrentContext(context) && requestId === cardRequestId) {
      cardAppointmentsError.value = true;
    }
  }
}

async function loadContactHistory(context) {
  contactRequestId += 1;
  const requestId = contactRequestId;
  if (!context.contactId) {
    if (isCurrentContext(context) && requestId === contactRequestId) {
      contactHistory.value = [];
      contactHistoryError.value = false;
    }
    return;
  }
  if (isCurrentContext(context)) contactHistoryError.value = false;
  try {
    const response = await AppointmentsAPI.contactHistory(
      context.accountId,
      context.contactId
    );
    if (!isCurrentContext(context) || requestId !== contactRequestId) return;
    contactHistory.value = response.data?.data || [];
  } catch {
    if (isCurrentContext(context) && requestId === contactRequestId) {
      contactHistoryError.value = true;
    }
  }
}

async function load(context = captureContext()) {
  if (!context.accountId || !context.pipelineCardId) return;
  loadOperationId += 1;
  const operationId = loadOperationId;
  loading.value = true;
  try {
    await Promise.all([
      loadCardAppointments(context),
      loadContactHistory(context),
    ]);
  } finally {
    if (isCurrentContext(context) && operationId === loadOperationId) {
      loading.value = false;
    }
  }
}

async function retryCardAppointments() {
  const context = captureContext();
  if (!context.accountId || !context.pipelineCardId) return;
  loadOperationId += 1;
  const operationId = loadOperationId;
  loading.value = true;
  try {
    await loadCardAppointments(context);
  } finally {
    if (isCurrentContext(context) && operationId === loadOperationId) {
      loading.value = false;
    }
  }
}

async function retryContactHistory() {
  const context = captureContext();
  if (!context.accountId || !context.pipelineCardId) return;
  loadOperationId += 1;
  const operationId = loadOperationId;
  loading.value = true;
  try {
    await loadContactHistory(context);
  } finally {
    if (isCurrentContext(context) && operationId === loadOperationId) {
      loading.value = false;
    }
  }
}

function formatDateTime(iso) {
  return new Intl.DateTimeFormat((locale.value || 'en-US').replace('_', '-'), {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: reportingTimezone.value,
  }).format(new Date(iso));
}

function formatPrice(cents, currency = 'BRL') {
  if (cents == null) return null;
  return new Intl.NumberFormat((locale.value || 'en-US').replace('_', '-'), {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

function handleCreated() {
  const context = captureContext();
  if (!context.accountId || !context.pipelineCardId) return;
  showModal.value = false;
  load(context);
  emit('appointmentCreated');
}

// Inline status actions on the card so agents don't have to leave to /appointments.
const TERMINAL_STATUSES = ['cancelled', 'completed', 'no_show'];

const ACTION_CLASS = {
  confirm: 'text-n-teal-11 bg-n-teal-3/40 hover:bg-n-teal-3',
  complete: 'text-n-brand bg-n-brand/10 hover:bg-n-brand/20',
  cancel: 'text-n-ruby-11 bg-n-ruby-3/40 hover:bg-n-ruby-3',
};
const actionLabels = computed(() => ({
  confirm: t('APPOINTMENTS.PIPELINE_BLOCK.ACTIONS.CONFIRM'),
  complete: t('APPOINTMENTS.PIPELINE_BLOCK.ACTIONS.COMPLETE'),
  cancel: t('APPOINTMENTS.PIPELINE_BLOCK.ACTIONS.CANCEL'),
}));

function availableActions(status) {
  if (TERMINAL_STATUSES.includes(status)) return [];
  const actions = [];
  if (status === 'scheduled') actions.push('confirm');
  actions.push('complete', 'cancel');
  return actions;
}

async function runAction(appt, action) {
  const context = captureContext();
  if (!context.accountId || !context.pipelineCardId) return;
  actionOperationId += 1;
  const operationId = actionOperationId;
  actionLoadingId.value = appt.id;
  actionLoadingAction.value = action;
  try {
    if (action === 'cancel') {
      await store.dispatch('appointmentsModule/cancel', {
        id: appt.id,
        accountId: context.accountId,
        generation: context.generation,
      });
    } else {
      await store.dispatch(`appointmentsModule/${action}`, {
        id: appt.id,
        accountId: context.accountId,
        generation: context.generation,
      });
    }
    if (!isCurrentContext(context) || operationId !== actionOperationId) return;
    await load(context);
    if (!isCurrentContext(context) || operationId !== actionOperationId) return;
    emit('appointmentCreated');
  } catch {
    if (isCurrentContext(context) && operationId === actionOperationId) {
      emitter.emit('newToastMessage', {
        message: t('APPOINTMENTS.PIPELINE_BLOCK.ACTION_ERROR'),
        action: { type: 'error' },
      });
    }
  } finally {
    if (isCurrentContext(context) && operationId === actionOperationId) {
      actionLoadingId.value = null;
      actionLoadingAction.value = null;
    }
  }
}

watch(
  [currentAccountId, () => props.pipelineCardId, () => props.contactId],
  ([accountId, pipelineCardId, contactId], previousContext = []) => {
    const [previousAccountId, previousPipelineCardId, previousContactId] =
      previousContext;
    const contextChanged =
      previousAccountId !== undefined &&
      (normalizeContextId(accountId) !==
        normalizeContextId(previousAccountId) ||
        normalizeContextId(pipelineCardId) !==
          normalizeContextId(previousPipelineCardId) ||
        normalizeContextId(contactId) !==
          normalizeContextId(previousContactId));
    contextGeneration += 1;
    cardRequestId += 1;
    contactRequestId += 1;
    loadOperationId += 1;
    actionOperationId += 1;
    resetContextState();
    if (contextChanged) {
      store.commit?.('appointmentsModule/RESET_ACCOUNT_DATA');
    }
    const context = captureContext();
    Promise.resolve().then(() => {
      if (isCurrentContext(context)) load(context);
    });
  },
  { immediate: true, flush: 'sync' }
);

onBeforeUnmount(() => {
  isUnmounted = true;
  contextGeneration += 1;
  cardRequestId += 1;
  contactRequestId += 1;
  loadOperationId += 1;
  actionOperationId += 1;
});
</script>

<template>
  <div class="border-t border-n-weak pt-4 mt-4">
    <!-- Header -->
    <div class="flex items-center justify-between mb-3 px-1">
      <h3
        class="text-[13px] font-bold text-n-slate-12 uppercase tracking-wide flex items-center gap-1.5"
      >
        <span
          class="i-lucide-calendar-clock size-4 text-n-slate-10"
          aria-hidden="true"
        />
        {{ t('APPOINTMENTS.PIPELINE_BLOCK.TITLE') }}
      </h3>
      <button
        type="button"
        class="inline-flex min-h-11 items-center gap-1 rounded-md bg-n-brand/10 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-n-brand transition-colors hover:bg-n-brand/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
        @click="showModal = true"
      >
        <span class="i-lucide-plus size-3" aria-hidden="true" />
        {{ t('APPOINTMENTS.PIPELINE_BLOCK.ADD') }}
      </button>
    </div>

    <!-- Loading -->
    <div
      v-if="loading"
      role="status"
      :aria-label="t('APPOINTMENTS.LOADING_DOTS')"
      class="space-y-2 px-1"
    >
      <div
        class="h-14 animate-pulse rounded-xl bg-n-alpha-1 motion-reduce:animate-none"
      />
      <div
        class="h-10 w-3/4 animate-pulse rounded-xl bg-n-alpha-1 motion-reduce:animate-none"
      />
    </div>

    <!-- Error state -->
    <div
      v-else-if="cardAppointmentsError"
      role="alert"
      class="flex flex-col items-center gap-3 rounded-xl border border-n-ruby-5 bg-n-ruby-2 p-4 text-center"
    >
      <span
        class="i-lucide-circle-alert size-5 text-n-ruby-10"
        aria-hidden="true"
      />
      <p class="text-[13px] font-medium text-n-ruby-11">
        {{ t('APPOINTMENTS.PIPELINE_BLOCK.LOAD_ERROR') }}
      </p>
      <button
        type="button"
        class="inline-flex min-h-11 items-center justify-center rounded-lg border border-n-ruby-6 bg-n-solid-1 px-4 py-2 text-xs font-semibold text-n-ruby-11 transition-colors hover:bg-n-ruby-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-ruby-8"
        @click="retryCardAppointments"
      >
        {{ t('APPOINTMENTS.PIPELINE_BLOCK.RETRY') }}
      </button>
    </div>

    <!-- Empty state -->
    <button
      v-else-if="appointments.length === 0"
      type="button"
      class="group flex min-h-11 w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-n-strong/50 bg-n-alpha-1/50 p-5 text-center transition-colors hover:bg-n-alpha-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
      @click="showModal = true"
    >
      <span
        class="i-lucide-calendar-plus size-6 text-n-slate-9 transition-colors duration-300 group-hover:text-n-brand"
        aria-hidden="true"
      />
      <span
        class="text-[13px] font-medium text-n-slate-11 transition-colors group-hover:text-n-slate-12"
      >
        {{ t('APPOINTMENTS.PIPELINE_BLOCK.EMPTY') }}
      </span>
    </button>

    <!-- Next upcoming highlight -->
    <div v-else-if="upcomingAppointment && !showAll" class="mb-3">
      <div
        class="group relative overflow-hidden rounded-xl border border-n-brand/20 bg-gradient-to-br from-n-brand/10 to-n-brand/5 p-3.5 shadow-sm ring-1 ring-n-brand/10 transition-shadow hover:shadow-md"
      >
        <!-- Shine effect on hover -->
        <div
          class="pointer-events-none absolute inset-0 -translate-x-[150%] bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-1000 ease-out group-hover:translate-x-[150%] motion-reduce:transform-none motion-reduce:transition-none"
        />

        <div class="flex flex-col gap-2 relative z-10">
          <div class="flex items-center justify-between gap-2">
            <StatusBadge :status="upcomingAppointment.status" />
            <span
              v-if="upcomingAppointment.price_cents"
              class="text-[11px] font-bold text-n-teal-11 bg-n-teal-3/50 px-2 py-0.5 rounded-md flex-shrink-0"
            >
              {{
                formatPrice(
                  upcomingAppointment.price_cents,
                  upcomingAppointment.currency
                )
              }}
            </span>
          </div>

          <div class="min-w-0">
            <p class="text-sm font-semibold text-n-slate-12 truncate">
              {{ upcomingAppointment.service?.name }}
            </p>
            <div
              class="flex flex-wrap items-center text-[12px] text-n-slate-11 mt-1 gap-x-2 gap-y-1"
            >
              <span class="flex items-center gap-1 font-medium text-n-slate-12">
                <span class="i-lucide-clock size-3" aria-hidden="true" />
                {{ formatDateTime(upcomingAppointment.scheduled_at) }}
              </span>
              <span
                v-if="upcomingAppointment.professional"
                class="flex items-center gap-1 truncate"
              >
                <span class="i-lucide-dot size-3" aria-hidden="true" />
                <span class="i-lucide-user size-3" aria-hidden="true" />
                <span class="truncate">{{
                  upcomingAppointment.professional.name
                }}</span>
              </span>
            </div>
          </div>

          <div
            v-if="availableActions(upcomingAppointment.status).length"
            class="flex items-center gap-1.5 pt-1"
          >
            <button
              v-for="action in availableActions(upcomingAppointment.status)"
              :key="action"
              type="button"
              :disabled="actionLoadingId === upcomingAppointment.id"
              :aria-busy="
                actionLoadingId === upcomingAppointment.id &&
                actionLoadingAction === action
              "
              class="inline-flex min-h-11 items-center gap-1.5 rounded-md px-3 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
              :class="ACTION_CLASS[action]"
              @click="runAction(upcomingAppointment, action)"
            >
              <span
                v-if="
                  actionLoadingId === upcomingAppointment.id &&
                  actionLoadingAction === action
                "
                class="i-lucide-loader-circle size-3 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
              {{ actionLabels[action] }}
            </button>
          </div>
        </div>
      </div>

      <div class="flex justify-center mt-2">
        <button
          v-if="appointments.length > 1"
          type="button"
          class="flex min-h-11 items-center gap-1 rounded-lg px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-n-slate-10 transition-colors hover:bg-n-alpha-1 hover:text-n-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          @click="showAll = true"
        >
          {{ t('APPOINTMENTS.PIPELINE_BLOCK.VIEW_ALL') }}
          <span class="bg-n-alpha-2 text-n-slate-12 px-1.5 rounded-full">{{
            appointments.length
          }}</span>
        </button>
      </div>
    </div>

    <!-- Full list -->
    <div v-else-if="showAll || !upcomingAppointment" class="space-y-2">
      <div
        v-for="appt in displayedAppointments"
        :key="appt.id"
        class="group rounded-xl border border-n-weak bg-n-background p-3 text-sm shadow-sm transition-[box-shadow,border-color,background-color] hover:border-n-strong hover:bg-n-alpha-1 hover:shadow-md"
      >
        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between gap-2">
            <StatusBadge :status="appt.status" />
            <span
              v-if="appt.price_cents"
              class="text-[10px] font-bold text-n-slate-10 bg-n-alpha-2 px-2 py-0.5 rounded-md flex-shrink-0"
            >
              {{ formatPrice(appt.price_cents, appt.currency) }}
            </span>
          </div>

          <div class="min-w-0">
            <p class="font-semibold text-n-slate-12 truncate text-[13px]">
              {{ appt.service?.name }}
            </p>
            <div
              class="flex flex-wrap items-center text-[11px] text-n-slate-10 mt-1 gap-x-2 gap-y-1"
            >
              <span class="flex items-center gap-1">
                {{ formatDateTime(appt.scheduled_at) }}
              </span>
              <span
                v-if="appt.professional"
                class="flex items-center gap-1 truncate"
              >
                <span class="i-lucide-dot size-3" aria-hidden="true" />
                {{ appt.professional.name }}
              </span>
            </div>
            <p
              v-if="appt.cancellation_reason"
              class="text-[11px] text-n-ruby-11 mt-1.5 bg-n-ruby-2 border border-n-ruby-4 rounded p-1.5 line-clamp-2"
            >
              {{ appt.cancellation_reason }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="appointments.length > 3 && !showAll" class="text-center pt-1">
        <button
          type="button"
          class="flex min-h-11 w-full items-center justify-center gap-1 rounded-lg px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-n-slate-10 transition-colors hover:bg-n-alpha-1 hover:text-n-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          @click="showAll = true"
        >
          {{ t('APPOINTMENTS.PIPELINE_BLOCK.VIEW_ALL') }}
          <span class="bg-n-alpha-2 text-n-slate-12 px-1.5 rounded-full">{{
            appointments.length
          }}</span>
        </button>
      </div>
    </div>

    <!-- Contact history error is independent from this card's appointments. -->
    <div
      v-if="!loading && contactId && contactHistoryError"
      role="alert"
      class="mt-5 flex flex-col items-start gap-2 rounded-xl border border-n-ruby-5 bg-n-ruby-2 p-4"
    >
      <p class="text-xs font-medium text-n-ruby-11">
        {{ t('APPOINTMENTS.PIPELINE_BLOCK.CONTACT_HISTORY_LOAD_ERROR') }}
      </p>
      <button
        type="button"
        class="inline-flex min-h-11 items-center justify-center rounded-lg border border-n-ruby-6 bg-n-solid-1 px-4 py-2 text-xs font-semibold text-n-ruby-11 transition-colors hover:bg-n-ruby-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-ruby-8"
        @click="retryContactHistory"
      >
        {{ t('APPOINTMENTS.PIPELINE_BLOCK.RETRY') }}
      </button>
    </div>

    <!-- Histórico do contato (appointments criados fora deste card) -->
    <div
      v-if="!contactHistoryError && otherContactAppointments.length"
      class="mt-5 pt-4 border-t border-dashed border-n-weak"
    >
      <h4
        class="text-[11px] font-bold text-n-slate-10 uppercase tracking-wider mb-2 flex items-center gap-1.5"
      >
        <span class="i-lucide-history size-3.5" aria-hidden="true" />
        {{ t('APPOINTMENTS.PIPELINE_BLOCK.CONTACT_HISTORY') }}
        <span
          class="bg-n-alpha-2 text-n-slate-11 px-1.5 rounded-full text-[10px]"
        >
          {{ otherContactAppointments.length }}
        </span>
      </h4>
      <div class="space-y-1.5">
        <div
          v-for="appt in otherContactAppointments"
          :key="`hist-${appt.id}`"
          class="rounded-lg border border-n-weak bg-n-alpha-1/40 p-2.5 text-xs hover:bg-n-alpha-1 transition-colors"
        >
          <div class="flex items-center justify-between gap-2 mb-1">
            <StatusBadge :status="appt.status" />
            <span
              class="text-[10px] text-n-slate-10 font-medium uppercase tracking-wide"
            >
              {{ t('APPOINTMENTS.PIPELINE_BLOCK.OTHER_SOURCE') }}
            </span>
          </div>
          <p class="font-semibold text-n-slate-12 truncate">
            {{ appt.service?.name }}
          </p>
          <div
            class="flex flex-wrap items-center text-[11px] text-n-slate-10 mt-0.5 gap-x-2"
          >
            <span>{{ formatDateTime(appt.scheduled_at) }}</span>
            <span
              v-if="appt.professional"
              class="inline-flex items-center gap-1 truncate"
            >
              <span class="i-lucide-dot size-3" aria-hidden="true" />
              {{ appt.professional.name }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Booking modal -->
    <AppointmentBookingModal
      :show="showModal"
      :prefill-contact-id="contactId"
      :pipeline-card-id="pipelineCardId"
      @close="showModal = false"
      @created="handleCreated"
    />
  </div>
</template>
